import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import { Menu, Dropdown, Badge, InputNumber, Modal } from "antd";
import {
  DownOutlined,
  UserOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  CheckOutlined,
  MinusCircleOutlined,
  PlusCircleOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import { LiaCalendarAltSolid } from "react-icons/lia";
import { DeleteOutlined } from "@ant-design/icons";
import { FaHotel } from "react-icons/fa6";
import { MdNightsStay } from "react-icons/md";
import { FaUser } from "react-icons/fa";
import { FaMapLocationDot } from "react-icons/fa6";
import { SlLink } from "react-icons/sl";
import { FaPhoneAlt } from "react-icons/fa";
import { GiShoppingCart } from "react-icons/gi";

import { fetchServiceData } from "../../Store/Apis/ReservationApi";
import PaymentModal from "./PaymentModal";
import { useLocation, useNavigate } from "react-router-dom";
import BookNowModal from "../GuestLandingPage/BookNowModal";
import dayjs from "dayjs";

const LuxuryReservation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeService, setActiveService] = useState(null);
  const [showBookNowModal, setShowBookNowModal] = useState(false);
  const { rates, reservationData } = location.state || {};
  const [services, setServices] = useState([]);

  useEffect(() => {
    const getServices = async () => {
      const serviceData = await fetchServiceData();
      setServices(serviceData);
    };
    getServices();
  }, []);

  // Check if this is a company booking
  const isCompanyBooking = reservationData?.bookingType === "company";
  const guestDetails = reservationData?.guestDetails || [];

  // Calculate overall dates and nights for company bookings
  const calculateCompanyBookingDetails = () => {
    if (!isCompanyBooking || guestDetails.length === 0) {
      return {
        overallStartDate: reservationData?.startDate || "",
        overallEndDate: reservationData?.endDate || "",
        overallNights: reservationData?.nights || 0,
        totalNights: reservationData?.nights || 0,
      };
    }

    // Find earliest check-in and latest check-out
    let earliestCheckIn = null;
    let latestCheckOut = null;

    guestDetails.forEach((guest) => {
      const checkIn = dayjs(guest.checkInDate);
      const checkOut = dayjs(guest.checkOutDate);

      if (!earliestCheckIn || checkIn.isBefore(earliestCheckIn)) {
        earliestCheckIn = checkIn;
      }
      if (!latestCheckOut || checkOut.isAfter(latestCheckOut)) {
        latestCheckOut = checkOut;
      }
    });

    // FIXED: Calculate overall nights as the span from earliest to latest date
    const overallNights =
      earliestCheckIn && latestCheckOut
        ? latestCheckOut.diff(earliestCheckIn, "day")
        : 0;

    return {
      overallStartDate: earliestCheckIn
        ? earliestCheckIn.format("YYYY-MM-DD")
        : "",
      overallEndDate: latestCheckOut ? latestCheckOut.format("YYYY-MM-DD") : "",
      overallNights: overallNights, // This is the correct total nights (15 in your example)
      totalNights: overallNights, // Same as overallNights for display
    };
  };

  const companyDetails = calculateCompanyBookingDetails();

  const {
    outletName = "",
    outletId = null,
    guests = 0,
    rooms = 0,
    nights = isCompanyBooking
      ? companyDetails.overallNights
      : reservationData?.nights || 0,
    startDate = isCompanyBooking
      ? companyDetails.overallStartDate
      : reservationData?.startDate || "",
    endDate = isCompanyBooking
      ? companyDetails.overallEndDate
      : reservationData?.endDate || "",
    roomSelections = {},
    additionalGuests = Math.max(0, guests - rooms),
  } = reservationData || {};

  useEffect(() => {
    if (!reservationData || !rates) {
      console.error("reservationData or rates are missing!");
    }
    console.log("Company booking details:", {
      isCompanyBooking,
      guestDetails,
      companyDetails,
      nights,
      startDate,
      endDate,
    });
  }, [reservationData, rates]);

  // UPDATED: Extract rate details with flat structure (no categories)
  const extractAllRateDetails = (rates) => {
    if (!rates) return [];

    const rateEntries = [];

    if (isCompanyBooking) {
      // FIXED: For company bookings, map each rate to its corresponding guest
      // Each rate should correspond to one guest/room
      const rateKeys = Object.keys(rates);

      rateKeys.forEach((key, index) => {
        const details = rates[key];

        // Find the corresponding guest for this rate (by index)
        const correspondingGuest = guestDetails[index];

        if (correspondingGuest) {
          const guestNights =
            correspondingGuest.nights ||
            dayjs(correspondingGuest.checkOutDate).diff(
              dayjs(correspondingGuest.checkInDate),
              "day"
            );
          const roomTypeName =
            details.typeName || (key.includes("_") ? key.split("_")[0] : key);

          rateEntries.push({
            id: `room-rate-${key
              .replace(/\s+/g, "-")
              .toLowerCase()}-guest-${index}`,
            name: `${roomTypeName} - ${correspondingGuest.fullName} (${guestNights} nights)`,
            price: details.price,
            quantity: 1,
            nights: guestNights,
            isRoom: true,
            guestIndex: index,
            guestName: correspondingGuest.fullName,
            originalKey: key,
          });
        }
      });
    } else {
      // For individual bookings, iterate directly over rates
      Object.entries(rates).forEach(([key, details]) => {
        // Extract the original room type name (remove the unique suffix)
        const roomTypeName =
          details.typeName || (key.includes("_") ? key.split("_")[0] : key);

        rateEntries.push({
          id: `room-rate-${key.replace(/\s+/g, "-").toLowerCase()}`,
          name: roomTypeName,
          price: details.price,
          quantity: 1,
          nights: nights,
          isRoom: true,
          originalKey: key,
        });
      });
    }

    console.log("Extracted rate entries (flat structure):", rateEntries);
    return rateEntries;
  };

  const rateInfo = extractAllRateDetails(rates);

  const [selectedItems, setSelectedItems] = useState({});

  const [cartDetails, setCartDetails] = useState({
    itemCount: 0,
    totalAmount: 0,
    items: [],
  });

  const [isPaymentModalVisible, setIsPaymentModalVisible] = useState(false);
  const [isCancelModalVisible, setIsCancelModalVisible] = useState(false);
  const cartSummaryRef = useRef(null);

  const aboutMenu = (
    <Menu>
      <Menu.Item key="1">
        <strong>Hotel Details</strong>
      </Menu.Item>
      <Menu.Item key="2">Phone: 017 714 0039</Menu.Item>
    </Menu>
  );

  const outletData = {
    3: {
      address: "5A Kieser St, Standerton\nSouth Africa, 2430",
      phone: "017 714 0039",
      website: "https://isiqaloguesthouse.co.za/",
    },
    4: {
      address: " Portion 9, Farm Verblyden, Standerton,\nSouth Africa, 8000",
      phone: "+27 66 520 8802",
      website: "isiqalo.reservations2@gscollege.edu.za",
    },
  };

  const outletDetails = outletData[outletId];

  const findAdditionalGuestAddon = () => {
    for (const service of services) {
      const additionalGuestItem = service.items.find(
        (item) =>
          item.name.toLowerCase().includes("additional guest") ||
          item.description?.toLowerCase().includes("additional guest")
      );

      if (additionalGuestItem) {
        return {
          serviceId: service.id,
          item: additionalGuestItem,
        };
      }
    }
    return null;
  };

  useEffect(() => {
    if (services.length > 0 && additionalGuests > 0 && !isCompanyBooking) {
      const additionalGuestAddon = findAdditionalGuestAddon();

      if (additionalGuestAddon) {
        const { item } = additionalGuestAddon;

        const isAlreadyInCart = cartDetails.items.some(
          (cartItem) => cartItem.id === item.id
        );

        if (!isAlreadyInCart) {
          setCartDetails((prevCart) => {
            const newItem = {
              id: item.id,
              name: `${item.name} (Auto-added)`,
              price: item.rate,
              quantity: additionalGuests,
              parentId: item.parentId,
              childId: item.childId,
              typeId: item.typeId,
            };

            const updatedItems = [...prevCart.items, newItem];
            const newTotalAmount = calculateCartTotal(updatedItems);

            return {
              itemCount: updatedItems.length,
              totalAmount: newTotalAmount,
              items: updatedItems,
            };
          });
        }
      }
    }
  }, [services, additionalGuests, isCompanyBooking]);

  // UPDATED: Calculate total considering individual nights for company bookings
  const calculateCartTotal = (items) => {
    return items.reduce((acc, item) => {
      if (item.isRoom) {
        // For rooms, use the individual nights stored in the item
        const itemNights = item.nights || nights;
        return acc + item.price * item.quantity * itemNights;
      }
      // For add-ons, just use regular price
      return acc + item.price * item.quantity;
    }, 0);
  };

  useEffect(() => {
    if (rateInfo.length > 0) {
      setCartDetails((prevCart) => {
        if (prevCart.items.length === 0) {
          const total = calculateCartTotal(rateInfo);
          return {
            itemCount: rateInfo.length,
            totalAmount: total,
            items: rateInfo,
          };
        }
        return prevCart;
      });
    }
  }, []);

  const toggleSelection = (itemId) => {
    setSelectedItems((prev) => {
      const allItems = services.flatMap((service) => service.items);
      const item = allItems.find((item) => item.id === itemId);

      if (!item) {
        console.error(`Item with ID ${itemId} not found!`);
        return prev;
      }

      if (prev[itemId]) {
        const { [itemId]: removed, ...rest } = prev;
        return rest;
      } else {
        return {
          ...prev,
          [itemId]: {
            quantity: 1,
            item: {
              ...item,
              parentId: item.parentId,
              childId: item.childId,
              typeId: item.typeId,
            },
          },
        };
      }
    });
  };

  const updateQuantity = (itemId, quantity) => {
    if (quantity <= 0) {
      setSelectedItems((prev) => {
        const { [itemId]: removed, ...rest } = prev;
        return rest;
      });
    } else {
      setSelectedItems((prev) => ({
        ...prev,
        [itemId]: {
          ...prev[itemId],
          quantity,
        },
      }));
    }
  };

  const calculateTotal = () => {
    let total = 0;

    // Add the room rates (already calculated in rateInfo)
    rateInfo.forEach((rate) => {
      const itemNights = rate.nights || nights;
      total += rate.price * itemNights;
    });

    // Add all selected items with their quantities
    Object.entries(selectedItems).forEach(([itemId, details]) => {
      total += details.item.rate * details.quantity;
    });

    return total;
  };

  const countSelectedItems = () => {
    return Object.keys(selectedItems).length;
  };

  const getActiveService = () => {
    return services.find((service) => service.id === activeService);
  };

  const scrollToCartSummary = () => {
    cartSummaryRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const removeFromCart = (itemId) => {
    if (itemId.startsWith("room-rate")) return;

    setSelectedItems((prev) => {
      const { [itemId]: removed, ...rest } = prev;
      return rest;
    });

    setCartDetails((prev) => {
      const updatedItems = prev.items.filter((item) => item.id !== itemId);
      const newTotalAmount = calculateCartTotal(updatedItems);

      return {
        itemCount: updatedItems.length,
        totalAmount: newTotalAmount,
        items: updatedItems,
      };
    });
  };

  useEffect(() => {
    if (rateInfo.length > 0) {
      setCartDetails({
        itemCount: rateInfo.length,
        totalAmount: calculateCartTotal(rateInfo),
        items: rateInfo,
      });
    }
  }, []);

  const handleAddToCart = () => {
    const cartItems = Object.entries(selectedItems).map(
      ([itemId, details]) => ({
        id: itemId,
        name: details.item.name,
        price: details.item.rate,
        quantity: details.quantity,
        parentId: details.item.parentId,
        childId: details.item.childId,
        typeId: details.item.typeId,
      })
    );

    setCartDetails((prevCartDetails) => {
      const existingItems = prevCartDetails.items.filter((item) => {
        return item.isRoom || !Object.keys(selectedItems).includes(item.id);
      });

      const combinedItems = [...existingItems];

      cartItems.forEach((newItem) => {
        const existingIndex = combinedItems.findIndex(
          (item) => item.id === newItem.id
        );
        if (existingIndex >= 0) {
          combinedItems[existingIndex] = newItem;
        } else {
          combinedItems.push(newItem);
        }
      });

      const newTotalAmount = calculateCartTotal(combinedItems);

      return {
        itemCount: combinedItems.length,
        totalAmount: newTotalAmount,
        items: combinedItems,
      };
    });

    setSelectedItems({});
    scrollToCartSummary();
  };

  // UPDATED: Show payment modal with proper pricing for company bookings
  const showPaymentModal = () => {
    console.log("Cart details when showing modal:", cartDetails);
    console.log("Is company booking:", isCompanyBooking);
    console.log("Company details:", companyDetails);

    setIsPaymentModalVisible(true);

    const selectedAddOns = cartDetails.items
      .filter((item) => !item.isRoom)
      .map((item) => ({
        parentId: item.parentId,
        childId: item.childId,
        typeId: item.typeId,
        name: item.name,
        rate: item.price,
        quantity: item.quantity || 1,
      }));

    const items = cartDetails.items.map((item) => item.name);

    // UPDATED: Calculate prices considering individual nights for each item
    const prices = cartDetails.items.map((item) => {
      if (item.isRoom) {
        const itemNights = item.nights || nights;
        return item.price * item.quantity * itemNights;
      } else {
        return item.price * item.quantity;
      }
    });

    console.log("Items being passed:", items);
    console.log("Prices being passed:", prices);

    setPaymentModalData({
      items,
      prices,
      cartItems: cartDetails.items,
      addOns: selectedAddOns,
    });
  };

  const [paymentModalData, setPaymentModalData] = useState({
    items: [],
    prices: [],
    cartItems: [],
    addOns: [],
  });

  const hidePaymentModal = () => setIsPaymentModalVisible(false);

  const showCancelModal = () => setIsCancelModalVisible(true);
  const hideCancelModal = () => setIsCancelModalVisible(false);

  const handleCancelBooking = () => {
    navigate("/");
  };

  const renderQuantityControls = (itemId) => {
    const itemDetails = selectedItems[itemId];
    const quantity = itemDetails ? itemDetails.quantity : 0;

    return (
      <QuantityControls>
        <QuantityButton
          onClick={(e) => {
            e.stopPropagation();
            if (quantity > 1) updateQuantity(itemId, quantity - 1);
          }}
        >
          <MinusCircleOutlined />
        </QuantityButton>

        <QuantityValue>{quantity}</QuantityValue>

        <QuantityButton
          onClick={(e) => {
            e.stopPropagation();
            updateQuantity(itemId, quantity + 1);
          }}
        >
          <PlusCircleOutlined />
        </QuantityButton>
      </QuantityControls>
    );
  };

  return (
    <Container>
      <NavBar>
        <LogoContainer>
          {outletId === 3 ? (
            <Logo src="/logo.png" alt="Logo" />
          ) : outletId === 4 ? (
            <Logo2 src="/LogoWaterfront.png" alt="Logo" />
          ) : (
            <>
              <Logo src="/logo.png" alt="Logo" />
              <Logo2 src="/LogoWaterfront.png" alt="Logo2" />
            </>
          )}
        </LogoContainer>

        <NavMenu>
          {showBookNowModal && (
            <BookNowModal onClose={() => setShowBookNowModal(false)} />
          )}

          <NavItem onClick={() => navigate("/")}>Home</NavItem>
          <Dropdown overlay={aboutMenu} trigger={["click"]}>
            <NavItem>
              About <DownOutlined />
            </NavItem>
          </Dropdown>

          <Badge
            count={cartDetails.itemCount}
            offset={[10, 0]}
            showZero
            style={{
              backgroundColor: "#b99028",
              color: "white",
              fontSize: "0.8rem",
              fontWeight: "bold",
            }}
          >
            <CartIcon onClick={showPaymentModal}>
              <GiShoppingCart style={{ color: "black" }} size={25} />
            </CartIcon>
          </Badge>
        </NavMenu>
      </NavBar>

      <MainContent>
        <HeroSection>
          <HotelInfoBox>
            <HotelName>{outletName}</HotelName>
            <HotelAddress>
              <LocationIcon>
                <FaMapLocationDot size={20} />
              </LocationIcon>
              {outletDetails?.address}
            </HotelAddress>
            <ContactInfo>
              <PhoneIcon>
                <FaPhoneAlt size={20} />
              </PhoneIcon>{" "}
              {outletDetails?.phone}
            </ContactInfo>
            <WebsiteLink>
              <LinkIcon>
                {" "}
                <SlLink size={20} />
              </LinkIcon>{" "}
              {outletDetails?.website}
            </WebsiteLink>
          </HotelInfoBox>
        </HeroSection>

        {/* UPDATED: Reservation Details Section with company booking info */}
        <ReservationBar>
          <DetailBox>
            <DetailIcon>
              <FaUser size={24} />
            </DetailIcon>
            <DetailLabel>Guests</DetailLabel>
            <DetailValue>{guests} Guests</DetailValue>
          </DetailBox>

          <DetailBox>
            <DetailIcon>
              <LiaCalendarAltSolid size={24} />
            </DetailIcon>
            <DetailLabel>Arrival</DetailLabel>
            <DetailValue>{startDate}</DetailValue>
          </DetailBox>

          <DetailBox>
            <DetailIcon>
              <LiaCalendarAltSolid size={24} />
            </DetailIcon>
            <DetailLabel>Departure</DetailLabel>
            <DetailValue>{endDate}</DetailValue>
          </DetailBox>

          <DetailBox>
            <DetailIcon>
              <FaHotel size={24} />
            </DetailIcon>
            <DetailLabel>Rooms</DetailLabel>
            <DetailValue>{rooms}</DetailValue>
          </DetailBox>

          <DetailBox>
            <DetailIcon>
              <MdNightsStay size={24} />
            </DetailIcon>
            <DetailLabel>Nights</DetailLabel>
            <DetailValue>
              {isCompanyBooking ? (
                <span
                  title={`Overall stay period: ${companyDetails.overallNights} nights`}
                >
                  {companyDetails.overallNights}
                </span>
              ) : (
                nights
              )}
            </DetailValue>
          </DetailBox>
        </ReservationBar>

        {/* UPDATED: Cart Summary with individual pricing display */}
        <CartSummary ref={cartSummaryRef}>
          <CartHeader>Your Cart: {cartDetails.itemCount} Item(s)</CartHeader>
          <TotalAmount>
            Total ZAR (ex vat) {cartDetails.totalAmount}
          </TotalAmount>

          {cartDetails.items.length > 0 && (
            <CartItemsList>
              {cartDetails.items.map((item, index) => (
                <CartItem key={index}>
                  <CartItemName>{item.name}</CartItemName>
                  <CartItemDetails>
                    <CartItemQuantity>x{item.quantity}</CartItemQuantity>
                    <CartItemPrice>
                      ZAR {item.price * item.quantity}
                      {item.isRoom && (
                        <NightMultiplier>
                          {" "}
                          x {item.nights || nights} nights = ZAR{" "}
                          {item.price * item.quantity * (item.nights || nights)}
                        </NightMultiplier>
                      )}
                    </CartItemPrice>
                    {!item.isRoom && (
                      <DeleteIcon onClick={() => removeFromCart(item.id)}>
                        🗑️
                      </DeleteIcon>
                    )}
                  </CartItemDetails>
                </CartItem>
              ))}
            </CartItemsList>
          )}

          {cartDetails.itemCount > 0 && (
            <ButtonContainer>
              <CancelButton onClick={showCancelModal}>
                Cancel Booking
              </CancelButton>
              <CheckoutButton onClick={showPaymentModal}>Submit</CheckoutButton>
            </ButtonContainer>
          )}
        </CartSummary>

        <AddOnsSection>
          <SectionTitle1>Add Ons</SectionTitle1>

          <ServiceCategories>
            {services.map((service) => (
              <ServiceCategory
                key={service.id}
                isActive={activeService === service.id}
                onClick={() =>
                  setActiveService(
                    activeService === service.id ? null : service.id
                  )
                }
              >
                <ServiceLabel>{service.name}</ServiceLabel>
                <ItemCount>{service.itemCount} Items</ItemCount>
              </ServiceCategory>
            ))}
          </ServiceCategories>

          {activeService && (
            <ServiceItemsSection>
              <ServiceHeader>
                Product Details - {getActiveService()?.name}
              </ServiceHeader>

              {activeService === "addon" ? (
                <AddOnsList>
                  {getActiveService()?.items.map((item) => (
                    <AddOnCard
                      key={item.id}
                      isSelected={!!selectedItems[item.id]}
                    >
                      <AddOnImage />
                      <AddOnDetails>
                        <AddOnName>{item.name}</AddOnName>
                        <AddOnDescription>{item.description}</AddOnDescription>
                        <AddOnPrice>ZAR {item.rate}</AddOnPrice>

                        {selectedItems[item.id] ? (
                          <>
                            <AddOnQuantityControl>
                              {renderQuantityControls(item.id)}
                            </AddOnQuantityControl>
                            <AddOnButton
                              isSelected={true}
                              onClick={() => toggleSelection(item.id)}
                            >
                              Remove from Cart
                            </AddOnButton>
                          </>
                        ) : (
                          <AddOnButton
                            isSelected={false}
                            onClick={() => toggleSelection(item.id)}
                          >
                            Add to Cart
                          </AddOnButton>
                        )}
                      </AddOnDetails>
                      {selectedItems[item.id] && (
                        <SelectionBadge>
                          <CheckOutlined />
                        </SelectionBadge>
                      )}
                    </AddOnCard>
                  ))}
                </AddOnsList>
              ) : (
                <ItemsGrid>
                  {getActiveService()?.items.map((item) => (
                    <ItemCard
                      key={item.id}
                      isSelected={!!selectedItems[item.id]}
                      onClick={() => toggleSelection(item.id)}
                    >
                      <ItemName>{item.name}</ItemName>
                      <ItemRate>Rate: ZAR {item.rate}</ItemRate>

                      {selectedItems[item.id] && (
                        <>
                          {renderQuantityControls(item.id)}
                          <SelectionIndicator>
                            <CheckOutlined />
                          </SelectionIndicator>
                        </>
                      )}
                    </ItemCard>
                  ))}
                </ItemsGrid>
              )}
            </ServiceItemsSection>
          )}

          {countSelectedItems() > 0 && (
            <OrderSummary>
              <SummaryDetails>
                <SummaryText>
                  Selected Items: {countSelectedItems()}
                </SummaryText>
                <TotalPrice>Total: ZAR {calculateTotal()}</TotalPrice>
              </SummaryDetails>
              <CheckoutButton onClick={handleAddToCart}>
                Add to Cart
              </CheckoutButton>
            </OrderSummary>
          )}
        </AddOnsSection>
      </MainContent>

      {/* Cancel Confirmation Modal */}
      <Modal
        title={
          <CancelModalTitle>
            <ExclamationCircleOutlined
              style={{ color: "#ff4d4f", marginRight: "8px" }}
            />
            Cancel Booking Confirmation
          </CancelModalTitle>
        }
        open={isCancelModalVisible}
        onCancel={hideCancelModal}
        footer={null}
        centered
        width={480}
        bodyStyle={{ padding: "24px" }}
      >
        <CancelModalContent>
          <WarningText>
            <strong>Are you sure you want to cancel this booking?</strong>
          </WarningText>
          <WarningDescription>
            This action cannot be undone. You will lose all your reservation
            information including:
          </WarningDescription>
          <WarningList>
            <li>Selected room(s) and dates</li>
            <li>Guest information and preferences</li>
            <li>Added services and amenities</li>
            <li>All pricing and booking details</li>
          </WarningList>
          <WarningFooter>
            You will need to start the booking process from the beginning.
          </WarningFooter>
          <CancelModalButtons>
            <CancelModalKeepButton onClick={hideCancelModal}>
              Keep Booking
            </CancelModalKeepButton>
            <CancelModalConfirmButton onClick={handleCancelBooking}>
              Yes, Cancel Booking
            </CancelModalConfirmButton>
          </CancelModalButtons>
        </CancelModalContent>
      </Modal>

      <PaymentModal
        visible={isPaymentModalVisible}
        onCancel={hidePaymentModal}
        items={paymentModalData.items}
        prices={paymentModalData.prices}
        cartItems={paymentModalData.cartItems}
        addOns={paymentModalData.addOns}
        nights={nights}
      />
    </Container>
  );
};

// Styled Components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  font-family: "Helvetica Neue", Arial, sans-serif;
  color: #333;
  background-color: #f5f5f5;
`;
// New styled component for the night multiplier indicator
const NightMultiplier = styled.span`
  display: inline-block;
  font-size: 0.85em;
  color: #666;
  margin-left: 5px;
`;
// Nav Bar styles
const NavBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 2rem;
  background-color: #f5f5f5;
  color: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center; /* Align logos vertically */
`;

const Logo = styled.img`
  height: 50px; /* Adjust the height of the logo */
  cursor: pointer;
`;

const Logo2 = styled.img`
  height: 50px; /* Adjust the height of the logo */
  cursor: pointer;
  margin-left: 10px; /* Add a tiny gap between logos */
`;
const NavMenu = styled.div`
  display: flex;
  align-items: center;
  gap: 2rem;
`;

const NavItem = styled.div`
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 500;
  letter-spacing: 0.5px;
  color: black;
  &:hover {
    color: #b99028;
  }
`;

const CartIcon = styled.div`
  font-size: 1.5rem; /* Increased size for better visibility */
  color: black !important;
  cursor: pointer;
`;

const MainContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const HeroSection = styled.div`
  position: relative;
  height: 400px; /* Set the height of the HeroSection */
  background-image: linear-gradient(rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2)),
    url("/Waterfront_landing2.jpg");
  background-size: cover; /* Make the image cover the entire container while maintaining aspect ratio */
  background-repeat: no-repeat; /* Prevent the image from repeating */
  background-position: center; /* Center the image within the section */
  display: flex;
  align-items: flex-start;
  padding: 2rem;
  overflow: hidden;

  /* Responsive height adjustments */
  @media (max-width: 768px) {
    height: 300px;
    padding: 1rem;
  }

  @media (max-width: 480px) {
    height: 250px;
    padding: 0.5rem;
  }
`;


const HotelInfoBox = styled.div`
  background-color: rgba(255, 255, 255, 0.9);
  padding: 1.5rem;
  border-radius: 4px;
  max-width: 400px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  margin-top: 149px;
`;

const HotelName = styled.h1`
  font-size: 1.8rem;
  font-weight: 600;
  margin: 0 0 1rem 0;
  color: #222;
`;

const HotelAddress = styled.div`
  display: flex;
  margin-bottom: 0.75rem;
  font-size: 0.9rem;
  line-height: 1.4;
`;

const LocationIcon = styled.span`
  margin-right: 0.5rem;
`;

const ContactInfo = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 0.75rem;
  font-size: 0.9rem;
`;

const PhoneIcon = styled.span`
  margin-right: 0.5rem;
`;

const WebsiteLink = styled.div`
  display: flex;
  align-items: center;
  font-size: 0.9rem;
  color: #0066cc;
`;

const LinkIcon = styled.span`
  margin-right: 0.5rem;
`;

const ReservationBar = styled.div`
  display: flex;
  padding: 1.5rem;
  background-color: white;
  border-bottom: 1px solid #e0e0e0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  flex-wrap: wrap;
`;

const DetailBox = styled.div`
  flex: 1;
  min-width: 200px;
  padding: 0.75rem 1.5rem;
  border-right: 1px solid #e0e0e0;
  display: flex;
  flex-direction: column;

  &:last-child {
    border-right: none;
  }
`;

const DetailIcon = styled.div`
  margin-bottom: 0.5rem;
  font-size: 1.2rem;
`;

const DetailLabel = styled.div`
  font-size: 0.8rem;
  color: #777;
  margin-bottom: 0.25rem;
`;

const DetailValue = styled.div`
  font-weight: 500;
`;

const CartSummary = styled.div`
  background-color: white;
  padding: 1.5rem;
  border-bottom: 1px solid #e0e0e0;
  text-align: right;
`;

const CartHeader = styled.div`
  font-size: 1.1rem;
  color: #777;
  margin-bottom: 0.5rem;
`;

const TotalAmount = styled.div`
  font-size: 1.5rem;
  font-weight: 600;
  color: #222;
`;

// Add-Ons Section Styles
const AddOnsSection = styled.div`
  padding: 2rem;
  background-color: #f9f9f9;
  margin-bottom: 2rem;
`;

const SectionTitle1 = styled.h2`
  font-size: 1.8rem;
  margin-bottom: 1.5rem;
  color: #222;
  font-weight: 600;
  position: relative; /* Required for the pseudo-element to position correctly */

  &:after {
    content: "";
    position: absolute;
    bottom: -10px;
    left: 0;
    width: 50px;
    height: 3px;
    background-color: #b99028; /* Gold color */
  }
`;

const ServiceCategories = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 2rem;
`;

const ServiceCategory = styled.div`
  border: 1px dashed ${(props) => (props.isActive ? "#b99028" : "#ccc")};
  padding: 0.75rem 1.25rem;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  background-color: ${(props) =>
    props.isActive ? "rgba(185, 144, 40, 0.1)" : "white"};

  &:hover {
    border-color: #b99028;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  }
`;

const ServiceLabel = styled.div`
  font-weight: 600;
  color: #666;
`;

const ItemCount = styled.div`
  color: #ff8c00;
  font-size: 0.9rem;
  font-weight: 500;
`;

const ServiceItemsSection = styled.div`
  margin-top: 1rem;
`;

const ServiceHeader = styled.h3`
  font-size: 1.2rem;
  margin-bottom: 1.5rem;
  color: #333;
  font-weight: 500;
`;

const ItemsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 1rem;
`;

const ItemCard = styled.div`
  border: 1px dashed ${(props) => (props.isSelected ? "#b99028" : "#ccc")};
  padding: 1rem;
  border-radius: 4px;
  position: relative;
  cursor: pointer;
  transition: all 0.2s ease;
  background-color: ${(props) =>
    props.isSelected ? "rgba(185, 144, 40, 0.1)" : "white"};

  &:hover {
    border-color: #b99028;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  }
`;

const ItemName = styled.h4`
  margin: 0 0 0.5rem 0;
  font-size: 0.95rem;
  font-weight: 500;
`;

const ItemRate = styled.div`
  color: #e53935;
  font-size: 0.9rem;
`;

const SelectionIndicator = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: #b99028;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
`;

// Styled components for your original add-ons design
const AddOnsList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
`;

const AddOnCard = styled.div`
  background-color: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: transform 0.2s;
  border: ${(props) =>
    props.isSelected ? "2px solid #b99028" : "1px solid #eee"};
  position: relative;

  &:hover {
    transform: translateY(-5px);
  }
`;

const AddOnImage = styled.div`
  height: 180px;
  background-image: url("/api/placeholder/400/180");
  background-size: cover;
  background-position: center;
`;

const AddOnDetails = styled.div`
  padding: 1.25rem;
`;

const AddOnName = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0 0 0.75rem 0;
  color: #222;
`;

const AddOnDescription = styled.p`
  font-size: 0.9rem;
  color: #555;
  margin-bottom: 1rem;
  line-height: 1.5;
`;

const AddOnPrice = styled.div`
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 1rem;
  color: #222;
`;

const AddOnButton = styled.button`
  background-color: ${(props) => (props.isSelected ? "#6c757d" : "#b99028")};
  color: white;
  border: none;
  padding: 0.6rem 1.2rem;
  border-radius: 4px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: ${(props) => (props.isSelected ? "#5a6268" : "#a07920")};
  }
`;

const SelectionBadge = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background-color: #b99028;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
`;

const OrderSummary = styled.div`
  margin-top: 2rem;
  padding: 1.5rem;
  border-top: 1px solid #eee;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
`;

const SummaryDetails = styled.div``;

const SummaryText = styled.p`
  margin: 0;
  color: #666;
`;

const TotalPrice = styled.p`
  margin: 0.5rem 0 0;
  font-size: 1.2rem;
  font-weight: 600;
  color: #333;
`;

const CheckoutButton = styled.button`
  background-color: #b99028;
  color: white;
  border: none;
  padding: 0.8rem 1.8rem;
  border-radius: 4px;
  font-weight: 500;
  cursor: pointer;
  margin-top: 1rem;
  transition: background-color 0.2s;

  &:hover {
    background-color: #a07920;
  }
`;

// Button container for cancel and submit buttons
const ButtonContainer = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
  justify-content: flex-end;
`;

// Cancel button styling
const CancelButton = styled.button`
  background-color: #084c7c;
  color: white;
  border: none;
  padding: 0.8rem 1.8rem;
  border-radius: 4px;
  font-weight: 500;
  cursor: pointer;
  margin-top: 1rem;
  transition: background-color 0.2s;

  &:hover {
    background-color: #ff4d4f;
    color: white;
    border-color: #ff4d4f;
  }
`;

// Cancel modal styled components
const CancelModalTitle = styled.div`
  display: flex;
  align-items: center;
  font-size: 1.2rem;
  font-weight: 600;
  color: #333;
`;

const CancelModalContent = styled.div`
  text-align: left;
`;

const WarningText = styled.div`
  font-size: 1.1rem;
  color: #333;
  margin-bottom: 1rem;
`;

const WarningDescription = styled.div`
  font-size: 1rem;
  color: #666;
  margin-bottom: 1rem;
  line-height: 1.5;
`;

const WarningList = styled.ul`
  color: #666;
  margin-bottom: 1rem;
  padding-left: 1.5rem;

  li {
    margin-bottom: 0.5rem;
    line-height: 1.4;
  }
`;

const WarningFooter = styled.div`
  font-size: 0.95rem;
  color: #888;
  font-style: italic;
  margin-bottom: 1.5rem;
`;

const CancelModalButtons = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
`;

const CancelModalKeepButton = styled.button`
  background-color: #b99028;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 4px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #a07920;
  }
`;

const CancelModalConfirmButton = styled.button`
  background-color: #084c7c;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 4px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #ff7875;
  }
`;

// Quantity control components
const QuantityControls = styled.div`
  display: flex;
  align-items: center;
  margin-top: 0.5rem;
`;

const QuantityButton = styled.button`
  background: none;
  border: none;
  color: #777;
  cursor: pointer;
  font-size: 1rem;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: #b99028;
  }
`;

const QuantityValue = styled.span`
  margin: 0 0.5rem;
  font-weight: 500;
  min-width: 1.5rem;
  text-align: center;
`;

// Cart item components
const CartItemsList = styled.div`
  margin-top: 1rem;
  text-align: left;
  border-top: 1px solid #eee;
  padding-top: 1rem;
`;

const CartItem = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.75rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #f5f5f5;
`;

const CartItemName = styled.div`
  font-weight: 500;
`;

const CartItemDetails = styled.div`
  display: flex;
  gap: 1rem;
`;

const CartItemQuantity = styled.div`
  color: #777;
`;

const CartItemPrice = styled.div`
  font-weight: 600;
`;

// Add-on quantity control
const AddOnQuantityControl = styled.div`
  margin-bottom: 1rem;
`;

const DeleteIcon = styled.div`
  cursor: pointer;
  color: #e53935;
  margin-left: 0.5rem;
  font-size: 1rem;
  &:hover {
    color: #c62828;
  }
`;

const BackButton = styled.button`
  background-color: #b99028;
  color: white;
  border: none;
  padding: 0.6rem 1.2rem;
  border-radius: 4px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
  font-size: 0.9rem;

  &:hover {
    background-color: #a07920;
  }
`;
export default LuxuryReservation;
